import { buildPrompt } from '@/ai/utils/promptBuilder'
import { transformWorkflow } from '@/ai/utils/transformWorkflow'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { getCardTypeInstruction } from '@/ai/prompts/common/instructions'
import { getJsonArrayOutputFormatting } from '@/ai/prompts/common/formatting'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'

export const buildWorkflowCardsPrompt = ({ workflow, cardTypes }) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []
  const cardTypeInstructionsContent = []
  const cardTypeConsiderationContent = []

  cardTypes.forEach((cardType) => {
    if (cardType.generateWithAI) {
      cardTypeInstructionsContent.push(getCardTypeInstruction(cardType))
      cardTypeConsiderationContent.push(cardType.title)
    }
  })

  promptSections.push({
    title: 'Request',
    content: 'For each event, generate the following information:',
    spacingAfter: 0
  })

  promptSections.push({
    contentType: 'bullet',
    content: cardTypeInstructionsContent
  })

  promptSections.push({
    title: 'Workflow Description',
    content: `\`${workflow.aiDescription}\`` || 'N/A'
  })

  const { events, entities, eventIdToLongIdMap } = transformWorkflow(workflow)

  promptSections.push({
    title: 'Current Workflow (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify({ domainEvents: events })
  })

  promptSections.push({
    title: 'Existing Entities (Optional): Use this information if applicable.',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify({ entities })
  })

  promptSections.push({
    title: 'Very Important Consideration',
    content: [
      `It is crucial to ensure that the output contains exactly ${workflow.eventsJson.length} JSON objects
       (one for each event). Each JSON object must carry the following information:`,
      ...cardTypeConsiderationContent
    ]
  })

  const formattingContent = `The resulting array must contain ${workflow.eventsJson.length} items. Description must be in human readable format, not in code format (Order Row, but not OrderRow).`

  const options = {
    shouldAddCards: true,
    shouldAddDataModels: false
  }

  const exampleData1 = {
    id: 1,
    commandExample: examples.commandExampleCreateOrder
  }
  const exampleData2 = {
    id: 2,
    commandExample: examples.commandExampleDeliverOrder
  }

  const eventsExample = [
    getEventExample(exampleData1, cardTypes, options),
    getEventExample(exampleData2, cardTypes, options)
  ]

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonArrayOutputFormatting(JSON.stringify(eventsExample), formattingContent)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return { messages, eventIdToLongIdMap }
}
